import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoLabel } from '../../Badges/InfoLabel';
import { Button } from '../../Fundamentals/Button';
import EmptyState from '../../Fundamentals/EmptyState/EmptyState';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import SegmentedControl from '../../Fundamentals/SegmentedControl/SegmentedControl';
import { withSek } from '../../PriceSimulation/worksheetFormatters';
import usePlanFormat from './usePlanFormat';
import { chargePlanProductCount, chargePlanTitle, chargePlanTotal, findChargePlan, pricedGroups } from './chargePlanUtils';
import { distinctTagValues, sortTiersByRank, tierProductCount } from './tierUtils';
import { VossPlanChangeBehaviourText } from '../../../Constants/VOSS/vossPlanEnums';
import type { PackageChargePlan, PackagePlan, PackageTier } from './Packages.types';

type Density = 'normal' | 'compact';

// the tier column stays put while the plan columns scroll sideways
const STICKY_COLUMN = 'left-0 z-index-1 border-right';
// inline because the design system's table cell rule (position: relative) outranks the position-sticky helper
const STICKY_POSITION = { position: 'sticky' } as const;

// a cell names only the first groups, so a tier with many of them doesn't stretch its whole row
const VISIBLE_GROUPS = 3;

interface ChargePlanCellProps {
    chargePlan: PackageChargePlan | undefined;
    tier: PackageTier;
    plan: PackagePlan;
    density: Density;
    onOpen: (chargePlan: PackageChargePlan) => void;
    onConfigure?: () => void;
}

const ChargePlanCell = ({ chargePlan, tier, plan, density, onOpen, onConfigure }: ChargePlanCellProps) => {
    const { t } = useTranslation();
    const [showAllGroups, setShowAllGroups] = useState(false);

    if (chargePlan === undefined)
        return onConfigure === undefined ? (
            <span className="d-block text-center text-secondary font-13 p-12">{t('packages.notConfigured')}</span>
        ) : (
            <button type="button" className="bg-transparent border-0 w-100 text-center text-secondary font-13 p-12 cursorpointer" onClick={onConfigure}>
                <SpirisIcon name="plus" size="sm" className="icon-neutral mr-8" />
                {t('packages.configure')}
            </button>
        );

    const groups = pricedGroups(chargePlan, tier);
    const hiddenCount = groups.length - VISIBLE_GROUPS;
    const price = withSek(chargePlanTotal(chargePlan));

    return (
        <div className="p-12">
            <button
                type="button"
                className="btn btn-tertiary btn-sm d-flex align-items-center gap-8 font-weight-semibold font-16 text-nowrap px-4 mx-n4"
                aria-label={`${chargePlanTitle(tier, plan)}, ${price}`}
                onClick={() => onOpen(chargePlan)}
            >
                <span className="text-success font-size-xs" aria-hidden="true">
                    ●
                </span>
                {price}
            </button>
            {density === 'normal' && (
                <>
                    <span className="d-block mt-4">
                        {(showAllGroups ? groups : groups.slice(0, VISIBLE_GROUPS)).map(({ group, isRemovedFromTier }) => (
                            <span key={group.id} className="d-block text-secondary font-13">
                                {isRemovedFromTier ? t('packages.removedFromTier') : group.name}
                            </span>
                        ))}
                    </span>
                    {hiddenCount > 0 && (
                        <button type="button" className="btn btn-tertiary btn-sm font-13 px-4 mx-n4" aria-expanded={showAllGroups} onClick={() => setShowAllGroups(!showAllGroups)}>
                            {showAllGroups ? t('packages.showFewerGroups') : t('packages.moreGroups', { count: hiddenCount })}
                        </button>
                    )}
                    <span className="d-block text-secondary font-13 mt-4 text-nowrap">
                        {t('packages.productGroupCount', { count: groups.length })} · {t('packages.productCount', { count: chargePlanProductCount(chargePlan) })}
                    </span>
                </>
            )}
        </div>
    );
};

interface PackageChargePlansTabProps {
    tiers: PackageTier[];
    plans: PackagePlan[];
    chargePlans: PackageChargePlan[];
    onOpen: (chargePlan: PackageChargePlan) => void;
    onAdd?: () => void;
    onConfigure?: (tier: PackageTier, plan: PackagePlan) => void;
}

const PackageChargePlansTab = ({ tiers, plans, chargePlans, onOpen, onAdd, onConfigure }: PackageChargePlansTabProps) => {
    const { t } = useTranslation();
    const { formatCycleLength } = usePlanFormat();
    const [density, setDensity] = useState<Density>('normal');

    const matrix = () => {
        if (tiers.length === 0 || plans.length === 0) return <EmptyState icon="spreadsheet" title={t('packages.noChargePlanMatrix')} />;

        return (
            <div className="overflow-auto border rounded-8">
                <table className="table mb-0">
                    <thead>
                        <tr>
                            <th className={STICKY_COLUMN} style={STICKY_POSITION}>
                                {t('packages.tierPlanHeader')}
                            </th>
                            {plans.map((plan) => (
                                <th key={plan.id} className="text-nowrap border-left">
                                    <span className="d-block font-weight-semibold">{plan.name}</span>
                                    <span className="d-block text-secondary font-13 font-weight-normal">
                                        {t('packages.period')}: {formatCycleLength(plan.subscriptionBillingPeriodCycle?.length)}
                                    </span>
                                    {plan.changeBehaviour !== undefined && plan.changeBehaviour !== null && (
                                        <span className="d-block text-secondary font-13 font-weight-normal">{t(VossPlanChangeBehaviourText[plan.changeBehaviour])}</span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortTiersByRank(tiers).map((tier, index) => (
                            <tr key={tier.id}>
                                <th scope="row" className={STICKY_COLUMN} style={STICKY_POSITION}>
                                    <span className="d-flex align-items-center gap-8">
                                        <span className="badge flex-shrink-0">{index + 1}</span>
                                        <span className="font-weight-semibold text-nowrap">{tier.name}</span>
                                    </span>
                                    {density === 'normal' && (
                                        <>
                                            <span className="d-flex flex-wrap gap-4 mt-8">
                                                {distinctTagValues(tier.tags ?? []).map((value) => (
                                                    <InfoLabel key={value} title={<code className="font-weight-normal font-size-xs">{value}</code>} />
                                                ))}
                                            </span>
                                            <span className="d-block text-secondary font-13 font-weight-normal mt-4">
                                                {t('packages.productCount', { count: tierProductCount(tier) })}
                                            </span>
                                        </>
                                    )}
                                </th>
                                {plans.map((plan) => (
                                    <td key={plan.id} className="p-0 border-left">
                                        <ChargePlanCell
                                            chargePlan={findChargePlan(chargePlans, tier.id, plan.id)}
                                            tier={tier}
                                            plan={plan}
                                            density={density}
                                            onOpen={onOpen}
                                            onConfigure={onConfigure === undefined ? undefined : () => onConfigure(tier, plan)}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <>
            <div className="d-flex align-items-start justify-content-between gap-16 flex-wrap mb-16">
                <div>
                    <h3 className="heading-lg mb-4">{t('packages.tabChargePlans')}</h3>
                    <p className="text-secondary font-13 mb-0">{t('packages.chargePlansDescription')}</p>
                </div>
                <div className="d-flex align-items-center gap-12 flex-wrap">
                    <SegmentedControl<Density>
                        size="sm"
                        className="mb-0"
                        ariaLabel={t('packages.density')}
                        value={density}
                        onChange={setDensity}
                        options={[
                            { value: 'normal', label: t('packages.densityNormal') },
                            { value: 'compact', label: t('packages.densityCompact') },
                        ]}
                    />
                    {onAdd !== undefined && (
                        <Button onClick={onAdd}>
                            <SpirisIcon name="plus" className="mr-8" />
                            {t('packages.addChargePlan')}
                        </Button>
                    )}
                </div>
            </div>
            {matrix()}
        </>
    );
};

export default PackageChargePlansTab;
